import gql from 'graphql-tag';
import { editorBlockFragment } from '@getaccept/editor-lib';
import { DocumentFragments } from './document.fragments';

export class DocumentQueries {
  static documentQuery = gql`
    query Document($documentId: String!) {
      document(documentId: $documentId) {
        ...documentFragment
      }
    }
    ${DocumentFragments.documentFragment}
  `;

  static documentWithoutAttachments = gql`
    query DocumentWithoutAttachments($documentId: String!) {
      document(documentId: $documentId) {
        ...documentWithoutAttachmentsFragment
      }
    }
    ${DocumentFragments.documentWithoutAttachmentsFragment}
  `;

  static documentsQuery = gql`
    query documents(
      $documentFilter: DocumentFilter!
      $userIds: [String!]
      $orderBy: String
      $limit: Int
      $offset: Int
      $tagIds: [String!]
      $withoutTags: Boolean
      $nameSearch: String
    ) {
      getDocuments(
        documentFilter: $documentFilter
        userIds: $userIds
        orderBy: $orderBy
        limit: $limit
        offset: $offset
        tagIds: $tagIds
        withoutTags: $withoutTags
        nameSearch: $nameSearch
      ) {
        ...documentListFragment
      }
    }
    ${DocumentFragments.documentListFragment}
  `;

  static reminderSettingsQuery = gql`
    query reminderSettings($documentId: String!) {
      document(documentId: $documentId) {
        id
        sendDate
        isSmsReminder
        isReminderSending
        reminderOpened
        reminderOpenedDays
        reminderSigned
        reminderSignedDays
        reminderRepeat
        reminderRepeatDays
        reminderExpiration
        reminderExpirationDays
      }
    }
  `;

  static documentParentIdQuery = gql`
    query DocumentParentId($documentId: String!) {
      document(documentId: $documentId) {
        id
        parentDocumentId
      }
    }
  `;

  static documentRevisionsQuery = gql`
    query DocumentRevisions($documentId: String!) {
      document(documentId: $documentId) {
        id
        documentRevisions {
          message
          revision
          status
          editorBlockContentVersions {
            blockId
            contentId
            versions {
              versionId
              lastModified
              isLatest
              isPublished
            }
          }
        }
      }
    }
  `;

  static loadDocumentPageQuery = gql`
    query DocumentPage($documentId: String!, $id: String!) {
      documentPage(documentId: $documentId, id: $id) {
        id
        thumbUrl
        pageType
        pageNum
        pageHeight
        pageWidth
        cropBoxH
        cropBoxW
        video {
          id
          thumbUrl
          videoUrl
        }
        contentLinkBlock {
          buttonText
          description
          id
          thumbUrl
          thumbExtension
          title
          url
        }
      }
    }
  `;

  static documentPreviewTokenQuery = gql`
    query previewToken($documentId: String!) {
      previewToken(documentId: $documentId) {
        jwt
        exp
      }
    }
  `;

  static pageThumbUrl = gql`
    query DocumentPageThumbUrl($id: String!, $documentId: String!) {
      documentPage(id: $id, documentId: $documentId) {
        id
        thumbUrl
      }
    }
  `;

  static pageNumQuery = gql`
    query DocumentPageNum($documentId: String!) {
      document(documentId: $documentId) {
        pages {
          id
          pageNum
        }
      }
    }
  `;

  static templateResourceDetailsQuery = gql`
    query TemplateResourceDetails($contentId: String!) {
      document(documentId: $contentId) {
        id
        name
        externalEditorType
        hasWriteAccess
      }
    }
  `;

  static documentPreviewImageUrl = gql`
    query DocumentPreviewImageUrl($documentId: String!) {
      documentPreviewImageUrl(documentId: $documentId)
    }
  `;

  static externalEditorIdQuery = gql`
    query ExternalEditorId($documentId: String!) {
      document(documentId: $documentId) {
        externalEditorId
      }
    }
  `;

  static documentPageQuery = gql`
    query DocumentPages($documentId: String!) {
      document(documentId: $documentId) {
        id
        pages {
          id
          thumbUrl
          pageType
          pageNum
          pageHeight
          pageWidth
          cropBoxH
          cropBoxW
          video {
            id
            thumbUrl
            videoUrl
            status
          }
          contentLinkBlock {
            buttonText
            description
            id
            thumbUrl
            thumbExtension
            title
            url
          }
          editorBlock {
            ...editorBlockFragment
          }
        }
      }
    }
    ${editorBlockFragment}
  `;

  static recipientsQuery = gql`
    query Document($documentId: String!) {
      document(documentId: $documentId) {
        id
        recipients {
          fullName
          mobile
          status
        }
      }
    }
  `;
  static DocumentStatusQuery = gql`
    query DocumentStatus($documentId: String!) {
      document(documentId: $documentId) {
        id
        status
      }
    }
  `;

  static addTagsMutation = gql`
    mutation AddDocumentTags($documentIds: [String!]!, $tagIds: [String!]!) {
      addDocumentTags(documentIds: $documentIds, tagIds: $tagIds) {
        tag {
          id
        }
        document {
          id
        }
      }
    }
  `;

  static eventLogQuery = gql`
    query eventLogs($documentId: String!, $eventName: String) {
      eventLogs(documentId: $documentId, eventName: $eventName) {
        userId
        recipientId
        tsaTime
        eventName
        eventMetaData
      }
    }
  `;

  static UserDocumentAccessQuery = gql`
    query userDocumentAccess($documentId: String!) {
      userDocumentAccess(documentId: $documentId) {
        entityId
        entityName
        accessType
      }
    }
  `;
  static accessToPrivateDocument = gql`
    query accessToPrivateDocument($documentId: String!) {
      document(documentId: $documentId) {
        id
      }
    }
  `;
  static documentQualifiedElectronicSignature = gql`
    query documentQualifiedElectronicSignature($id: String!) {
      getDocument(id: $id) {
        settings {
          qualifiedElectronicSignature
        }
      }
    }
  `;

  static recipientsDetailed = gql`
    query RecipientsDetailed($documentId: String!) {
      document(documentId: $documentId) {
        id
        recipients {
          ...recipientFragment
        }
      }
    }
    ${DocumentFragments.recipientFragment}
  `;
}
